import { ManagedServiceId } from '@axinom/mosaic-id-utils';
import {
  type ActionData,
  DateRenderer,
  DynamicDataList,
  type DynamicListColumn,
  IconName,
  PageHeaderActionType,
} from '@axinom/mosaic-ui';
import React, { type ReactNode, useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useRowDataListDataEntry } from './RowDataListEntry';
import type { DeploymentData, ServiceDataTypes } from './RowExplorer.types';
import { useRowSelectExplorerModal } from './RowExplorerModel';

interface RowDataListProps {
  /** Seasons of the Tv Show */
  value: ServiceDataTypes[];
  /** Maximum number of items which can be assigned */
  maxItems?: number;
  /** Raised when the list has changed */
  onChange: (values: DeploymentData[]) => void;

  type: string;

  disabled: boolean;
}

export const RowDataList: React.FC<RowDataListProps> = ({
  maxItems,
  value,
  onChange,
  type,
  disabled,
}) => {
  const { envId, customizableServiceId } = useParams<{
    envId: string;
    customizableServiceId: string;
  }>();

  const { RowDataListDataEntry } = useRowDataListDataEntry(
    {
      excludeItems: value,
    },
    type,
  );

  const handleUnassign = useCallback(
    (id: ServiceDataTypes['id']) => {
      onChange(value.filter((val: { id: ServiceDataTypes }) => val.id !== id));
    },
    [onChange, value],
  );

  const getColumnsByType = (
    type: string,
  ): DynamicListColumn<DeploymentData['id']>[] => {
    switch (type) {
      case 'ContainerImage':
        return [
          {
            label: 'Repository Name',
            propertyName: 'repositoryName',
            size: '2fr',
            render: (_val: unknown, data: DeploymentData['id']) =>
              typeof data.containerImageVersion === 'string' &&
              data.containerImageVersion.split(':')[0],
          },
          {
            label: 'Container Image Version',
            propertyName: 'containerImageVersion',
            size: '1fr',
            render: (val: unknown) =>
              typeof val === 'string' && val.split(':')[1],
          },
        ];
      case 'ServicePiletArtifacts':
        return [
          {
            label: 'Name',
            propertyName: 'name',
            size: '2fr',
            render: TitleRenderer,
          },
          {
            label: 'Version',
            propertyName: 'packageVersion',
            size: '1fr',
          },
        ];
      case 'ServiceDeploymentManifest':
        return [
          {
            label: 'Name',
            propertyName: 'name',
            size: '2fr',
          },
          {
            label: 'Created At',
            propertyName: 'createdDate',
            render: DateRenderer,
            size: '1fr',
          },
        ];
      default:
        return [];
    }
  };

  const [replaceValue, setReplaceValue] = useState<string>();

  const {
    ModalWrapper: RowSelectExplorerModal,
    openModal,
    closeModal,
  } = useRowSelectExplorerModal({
    excludeItems: value?.map((item) => item?.id),
    onSelection: (selection) => {
      if (selection.mode === 'SINGLE_ITEMS') {
        const items = selection.items ?? [];
        onChange(
          value.map((val) => (replaceValue !== items[0].id ? items[0] : val)),
        );
      }
      closeModal();
    },
    type,
  });

  const handleReplace = useCallback(
    (id: string) => {
      setReplaceValue(id);
      openModal();
    },
    [openModal],
  );

  const generateInlineMenuActions: (data: DeploymentData) => ActionData[] = (
    data,
  ) => {
    const actions = [];
    if (!disabled) {
      actions.push(
        {
          label: 'Unassign',
          onActionSelected: () => handleUnassign(data.id),
          actionType: PageHeaderActionType.Context,
          icon: IconName.X,
        },
        {
          label: 'Replace',
          onActionSelected: () => handleReplace(data.id),
          icon: IconName.Replace,
        },
      );
    } else {
      if (type === 'ServiceDeploymentManifest') {
        actions.push({
          label: 'Open Details',
          path: `/env/${envId}/services/${ManagedServiceId.HOSTING_SERVICE}/config/customizable-services/${customizableServiceId}/deployment-manifests/${data.id}`,
        });
      }
    }
    return actions;
  };

  return (
    <>
      <DynamicDataList<DeploymentData>
        value={value ?? ['Not Available']}
        columns={getColumnsByType(type)}
        onChange={onChange}
        allowReordering={false}
        showHeader={false}
        allowNewData={
          value?.length === 0 ||
          maxItems === undefined ||
          (value?.length > 0 && value?.length < maxItems)
        }
        customDataEntry={RowDataListDataEntry}
        stickyHeader={false}
        inlineMenuActions={generateInlineMenuActions}
      />
      <RowSelectExplorerModal />
    </>
  );
};

const TitleRenderer = (val: unknown): ReactNode => {
  if (!val) {
    return <div>None</div>;
  }
  return val;
};
